import { all, call, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import {
    adminCampaignHostSubscriptionsUrl,
    adminCancelCampaignHostSubscriptionUrl,
    adminCancelCreatorSubscriptionUrl,
    adminCreatorSubscriptionPlansUrl,
    adminCreatorSubscriptionsUrl,
    adminExpireCreatorTrialUrl,
    adminResetCreatorTrialUrl,
    adminSwitchCreatorPaystackPlanUrl,
    adminChargeCreatorPaystackAuthorizationUrl,
    campaignHostPackagesUrl,
    eventHostPackagesUrl,
    getSubscriptionPlansFlutterwaveUrl,
} from '../../configurations/api/url';
import { SentryCapture } from '../../analytics/Sentry';
import { httpRequest } from '../types';
import {
    buildAdminCreatorSubscriptionListQuery,
    parseAdminCreatorSubscriptionListBody,
} from '../../utils/adminCreatorSubscriptionDirectory';
import {
    normalizeCreatorGrantablePlans,
    preferGrantablePlansForPicker,
} from '../../utils/adminCreatorSubscriptionGrant';
import {
    normalizeFlutterwaveCatalogItem,
    normalizeFlutterwaveSyncResult,
    syncFlutterwavePlansUrl,
    unwrapFlutterwaveList,
    type FlutterwaveSyncKind,
    type SyncFlutterwavePlansInput,
} from '../../utils/adminFlutterwavePlanSync';
import {
    cancelAdminCampaignHostSubscription,
    cancelAdminCampaignHostSubscriptionFail,
    cancelAdminCampaignHostSubscriptionSuccess,
    cancelAdminCreatorSubscription,
    cancelAdminCreatorSubscriptionFail,
    cancelAdminCreatorSubscriptionSuccess,
    chargeAdminCreatorPaystackAuthorization,
    chargeAdminCreatorPaystackAuthorizationFail,
    chargeAdminCreatorPaystackAuthorizationSuccess,
    expireAdminCreatorTrial,
    expireAdminCreatorTrialFail,
    expireAdminCreatorTrialSuccess,
    fetchAdminPaystackSwitchPlans,
    fetchAdminPaystackSwitchPlansFail,
    fetchAdminPaystackSwitchPlansSuccess,
    fetchAdminSubscriptionsByKind,
    fetchAdminSubscriptionsByKindFail,
    fetchAdminSubscriptionsByKindSuccess,
    fetchFlutterwaveSyncCatalog,
    fetchFlutterwaveSyncCatalogFail,
    fetchFlutterwaveSyncCatalogSuccess,
    resetAdminCreatorTrial,
    resetAdminCreatorTrialFail,
    resetAdminCreatorTrialSuccess,
    switchAdminCreatorPaystackPlan,
    switchAdminCreatorPaystackPlanFail,
    switchAdminCreatorPaystackPlanSuccess,
    syncFlutterwavePlansAdmin,
    syncFlutterwavePlansAdminFail,
    syncFlutterwavePlansAdminSuccess,
} from './actions';
import type {
    AdminSubscriptionKind,
    IAdminCreatorSubscriptionListQuery,
    ISwitchAdminCreatorPaystackPlanPayload,
} from '../../utils/adminCreatorSubscriptionDirectory';

function settle(
    resolve: ((v: any) => void) | undefined,
    reject: ((e: any) => void) | undefined,
    value: any,
    isError = false,
) {
    if (isError) {
        if (typeof reject === 'function') reject(value);
        return;
    }
    if (typeof resolve === 'function') resolve(value);
}

function* fetchByKindSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { kind: AdminSubscriptionKind; params?: IAdminCreatorSubscriptionListQuery };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const kind = payload.kind;
        const params = payload.params || {};
        const base = kind === 'campaign_host' ? adminCampaignHostSubscriptionsUrl : adminCreatorSubscriptionsUrl;
        const body = yield call(
            api,
            `${base}${buildAdminCreatorSubscriptionListQuery(params)}`,
            httpRequest.GET,
            null,
            2,
            0,
        );
        const parsed = parseAdminCreatorSubscriptionListBody(body);
        const data =
            kind === 'campaign_host'
                ? {
                      ...parsed,
                      rows: parsed.rows.map(r => ({ ...r, kind: r.kind ?? 'campaign_host' })),
                      filters: parsed.filters ?? {
                          status: ['active', 'trialing', 'past_due', 'canceled'],
                          planClass: [],
                          source: ['stripe', 'paystack', 'admin_grant'],
                      },
                  }
                : {
                      ...parsed,
                      rows: parsed.rows.map(r => ({ ...r, kind: r.kind ?? 'creator' })),
                  };
        yield put(fetchAdminSubscriptionsByKindSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchAdminSubscriptionsByKindFail(error));
        settle(resolve, reject, error, true);
    }
}

function* expireTrialSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { subscriptionId: string; mode?: 'convert' | 'cancel' };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const mode = payload.mode ?? 'convert';
        const body = yield call(
            api,
            adminExpireCreatorTrialUrl(payload.subscriptionId),
            httpRequest.POST,
            { mode },
            2,
            0,
        );
        const data = body?.data ?? body ?? {};
        yield put(expireAdminCreatorTrialSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(expireAdminCreatorTrialFail(error));
        settle(resolve, reject, error, true);
    }
}

function* cancelCreatorSaga({
    payload,
    resolve,
    reject,
}: {
    payload: string;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const body = yield call(api, adminCancelCreatorSubscriptionUrl(payload), httpRequest.POST, {}, 2, 0);
        const data = body?.data ?? body ?? {};
        yield put(cancelAdminCreatorSubscriptionSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(cancelAdminCreatorSubscriptionFail(error));
        settle(resolve, reject, error, true);
    }
}

function* cancelCampaignHostSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { subscriptionId: string; cancelImmediately?: boolean };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const cancelImmediately = payload.cancelImmediately !== false;
        const body = yield call(
            api,
            adminCancelCampaignHostSubscriptionUrl(payload.subscriptionId),
            httpRequest.POST,
            { cancelImmediately },
            2,
            0,
        );
        const data = body?.data ?? body ?? {};
        yield put(cancelAdminCampaignHostSubscriptionSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(cancelAdminCampaignHostSubscriptionFail(error));
        settle(resolve, reject, error, true);
    }
}

function* resetTrialSaga({
    payload,
    resolve,
    reject,
}: {
    payload: string;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        yield call(api, adminResetCreatorTrialUrl(payload), httpRequest.POST, {}, 2, 0);
        yield put(resetAdminCreatorTrialSuccess());
        settle(resolve, reject, undefined);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(resetAdminCreatorTrialFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchPaystackPlansSaga({
    resolve,
    reject,
}: {
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const body = yield call(
            api,
            `${adminCreatorSubscriptionPlansUrl}?source=paystack`,
            httpRequest.GET,
            null,
            2,
            0,
        );
        const raw = body?.data ?? body;
        const plans = preferGrantablePlansForPicker(normalizeCreatorGrantablePlans(raw));
        yield put(fetchAdminPaystackSwitchPlansSuccess(plans));
        settle(resolve, reject, plans);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchAdminPaystackSwitchPlansFail(error));
        settle(resolve, reject, error, true);
    }
}

function* switchPaystackPlanSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { subscriptionId: string; payload: ISwitchAdminCreatorPaystackPlanPayload };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const body = yield call(
            api,
            adminSwitchCreatorPaystackPlanUrl(payload.subscriptionId),
            httpRequest.POST,
            payload.payload,
            2,
            0,
        );
        const data = body?.data ?? body ?? {};
        yield put(switchAdminCreatorPaystackPlanSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(switchAdminCreatorPaystackPlanFail(error));
        settle(resolve, reject, error, true);
    }
}

function* chargePaystackAuthSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { subscriptionId: string; force?: boolean };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const body = yield call(
            api,
            adminChargeCreatorPaystackAuthorizationUrl(payload.subscriptionId),
            httpRequest.POST,
            { force: Boolean(payload.force) },
            2,
            0,
        );
        const data = body?.data ?? body ?? {};
        yield put(chargeAdminCreatorPaystackAuthorizationSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(chargeAdminCreatorPaystackAuthorizationFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchFlutterwaveCatalogSaga({
    payload,
    resolve,
    reject,
}: {
    payload: FlutterwaveSyncKind;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        let url = campaignHostPackagesUrl;
        if (payload === 'event_host') url = eventHostPackagesUrl;
        if (payload === 'creator') url = `${getSubscriptionPlansFlutterwaveUrl}?limit=100`;
        const response = yield call(api, url, httpRequest.GET, null, 2);
        let rows = unwrapFlutterwaveList(response)
            .map(row => normalizeFlutterwaveCatalogItem((row || {}) as Record<string, unknown>))
            .filter((r): r is NonNullable<typeof r> => Boolean(r));
        if (payload === 'campaign_host' || payload === 'event_host') {
            rows = rows.filter(r => !r.billing_type || r.billing_type === 'recurring');
        }
        yield put(fetchFlutterwaveSyncCatalogSuccess(rows));
        settle(resolve, reject, rows);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchFlutterwaveSyncCatalogFail(error));
        settle(resolve, reject, error, true);
    }
}

function* syncFlutterwavePlansSaga({
    payload,
    resolve,
    reject,
}: {
    payload: SyncFlutterwavePlansInput;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const url = syncFlutterwavePlansUrl(payload.kind, payload.packageId);
        const body: Record<string, unknown> = {
            usd_amount_cents: Math.round(payload.usd_amount_cents),
            dry_run: Boolean(payload.dry_run),
            skip_existing: payload.skip_existing !== false,
        };
        if (payload.name?.trim()) body.name = payload.name.trim();
        if (payload.interval?.trim()) body.interval = payload.interval.trim();
        if (payload.currencies?.length) body.currencies = payload.currencies;
        const response = yield call(api, url, httpRequest.POST, body, 2);
        const data = normalizeFlutterwaveSyncResult(response, payload.kind);
        yield put(syncFlutterwavePlansAdminSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(syncFlutterwavePlansAdminFail(error));
        settle(resolve, reject, error, true);
    }
}

export default function* adminSubscriptionsSaga() {
    yield all([
        takeLatest(fetchAdminSubscriptionsByKind as any, fetchByKindSaga),
        takeLatest(expireAdminCreatorTrial as any, expireTrialSaga),
        takeLatest(cancelAdminCreatorSubscription as any, cancelCreatorSaga),
        takeLatest(cancelAdminCampaignHostSubscription as any, cancelCampaignHostSaga),
        takeLatest(resetAdminCreatorTrial as any, resetTrialSaga),
        takeLatest(fetchAdminPaystackSwitchPlans as any, fetchPaystackPlansSaga),
        takeLatest(switchAdminCreatorPaystackPlan as any, switchPaystackPlanSaga),
        takeLatest(chargeAdminCreatorPaystackAuthorization as any, chargePaystackAuthSaga),
        takeLatest(fetchFlutterwaveSyncCatalog as any, fetchFlutterwaveCatalogSaga),
        takeLatest(syncFlutterwavePlansAdmin as any, syncFlutterwavePlansSaga),
    ]);
}
